import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Users, FileText, DollarSign, TrendingUp, Activity, 
  AlertTriangle, CheckCircle, Clock, Eye, Search, Filter, Settings,
  Edit, Trash2, Plus, RefreshCw, Package, CreditCard, Menu, X, Scale,
  UserX, UserCheck
} from 'lucide-react';
import { API_BASE, apiRequest } from '../../utils/api';
import { roleLabel } from '../../utils/userPermissions';
import ConfirmModal from '../shared/ConfirmModal';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [contracts, setContracts] = useState({ contracts: [], total: 0 });
  const [activities, setActivities] = useState({ logs: [], total: 0 });
  const [plans, setPlans] = useState({ plans: [], total: 0 });
  const [payments, setPayments] = useState({ payments: [], total: 0 });
  const [lawyers, setLawyers] = useState({ profiles: [], total: 0 });
  const [userReports, setUserReports] = useState({ reports: [], total: 0 });
  const [lawyerStatusFilter, setLawyerStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Modal states
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [userToActivate, setUserToActivate] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);

  const emptyStats = {
    total_users: 0,
    active_users: 0,
    total_documents: 0,
    total_contracts: 0,
    total_revenue: 0,
    avg_risk_score: 0,
    active_users_today: 0,
    contracts_processed_today: 0,
    top_risk_types: [],
    pending_lawyer_profiles: 0,
    successful_payments: 0,
    documents_today: 0,
    pending_user_reports: 0,
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError('');

      const responses = await Promise.allSettled([
        apiRequest('admin/stats'),
        apiRequest('admin/users?limit=50'),
        apiRequest('admin/documents?limit=50'),
        apiRequest('admin/activity-logs?limit=50'),
        apiRequest('admin/subscription-plans'),
        apiRequest('admin/payments?limit=50'),
        apiRequest('admin/lawyers?limit=50'),
        apiRequest('admin/user-reports?status=pending&limit=50'),
      ]);

      if (responses[0].status === 'fulfilled') setStats(responses[0].value);
      else setStats(emptyStats);
      if (responses[1].status === 'fulfilled') {
        const usersArr = Array.isArray(responses[1].value) ? responses[1].value : [];
        setUsers({ users: usersArr, total: usersArr.length });
      }
      if (responses[2].status === 'fulfilled') setContracts(responses[2].value);
      if (responses[3].status === 'fulfilled') setActivities(responses[3].value);
      if (responses[4].status === 'fulfilled') {
        const plansData = responses[4].value;
        setPlans({
          plans: Array.isArray(plansData?.plans) ? plansData.plans : [],
          total: plansData?.total ?? (plansData?.plans?.length || 0),
        });
      }
      if (responses[5].status === 'fulfilled') setPayments(responses[5].value);
      if (responses[6].status === 'fulfilled') setLawyers(responses[6].value);
      if (responses[7].status === 'fulfilled') {
        setUserReports({
          reports: responses[7].value?.reports || [],
          total: responses[7].value?.total || 0,
        });
      }

    } catch (error) {
      setError(error.message || 'Failed to fetch admin data');
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewReport = async (reportId) => {
    try {
      await apiRequest(`admin/user-reports/${reportId}/review`, { method: 'PUT' });
      setUserReports((prev) => ({
        reports: prev.reports.filter((r) => r.id !== reportId),
        total: Math.max(0, (prev.total || 0) - 1),
      }));
      setStats((prev) =>
        prev
          ? { ...prev, pending_user_reports: Math.max(0, (prev.pending_user_reports || 0) - 1) }
          : prev
      );
    } catch (err) {
      setError(err.message || 'Failed to mark report reviewed');
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      await apiRequest(`admin/users/${userId}/deactivate`, {
        method: 'PUT',
      });
      await fetchAdminData();
    } catch (error) {
      console.error('Error deactivating user:', error);
      alert(error.message || 'Error deactivating user.');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await apiRequest(`admin/users/${userId}/activate`, {
        method: 'PUT',
      });
      await fetchAdminData();
    } catch (error) {
      console.error('Error activating user:', error);
      alert(error.message || 'Error activating user.');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await apiRequest(`admin/users/${userId}`, {
        method: 'DELETE'
      });
      await fetchAdminData();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.message || 'Error deleting user.');
    }
  };

  const fetchLawyers = async (status = lawyerStatusFilter) => {
    try {
      const query = status && status !== 'all'
        ? `admin/lawyers?status=${status}&limit=50`
        : 'admin/lawyers?limit=50';
      const data = await apiRequest(query);
      setLawyers(data);
    } catch (error) {
      console.error('Error fetching lawyers:', error);
    }
  };

  const handleApproveLawyer = async (profileId) => {
    try {
      await apiRequest(`admin/lawyers/${profileId}/approve`, { method: 'PUT' });
      await fetchLawyers();
    } catch (error) {
      alert(error.message || 'Failed to approve profile');
    }
  };

  const handleRejectLawyer = async (profileId, { revoke = false } = {}) => {
    const reason = window.prompt(
      revoke
        ? 'Reason for revoking this approved profile (sent to the lawyer):'
        : 'Rejection reason (sent to the lawyer):',
    );
    if (!reason || reason.trim().length < 4) return;
    try {
      await apiRequest(`admin/lawyers/${profileId}/reject`, {
        method: 'PUT',
        body: { reason: reason.trim() },
      });
      await fetchLawyers();
    } catch (error) {
      alert(error.message || (revoke ? 'Failed to revoke profile' : 'Failed to reject profile'));
    }
  };

  const handleCreatePlan = async (planData) => {
    try {
      await apiRequest('admin/subscription-plans', {
        method: 'POST',
        body: planData
      });
      await fetchAdminData();
      setShowAddPlanModal(false);
    } catch (error) {
      console.error('Error creating plan:', error);
      alert(error.message || 'Error creating plan.');
    }
  };

  const handleEditPlan = async (planId, planData) => {
    try {
      await apiRequest(`admin/subscription-plans/${planId}`, {
        method: 'PUT',
        body: planData
      });
      await fetchAdminData();
      setShowEditPlanModal(false);
      setEditingPlan(null);
    } catch (error) {
      console.error('Error updating plan:', error);
      alert(error.message || 'Error updating plan.');
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await apiRequest(`admin/subscription-plans/${planId}`, {
        method: 'DELETE'
      });
      await fetchAdminData();
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert(error.message || 'Error deleting plan.');
    }
  };

  // Defensive: ensure all data objects have default structure
  const safeUsers = users && Array.isArray(users.users) ? users : { users: [], total: 0 };
  const safeContracts = contracts && Array.isArray(contracts.contracts) ? contracts : { contracts: [], total: 0 };
  const safePayments = payments && Array.isArray(payments.payments) ? payments : { payments: [], total: 0 };
  const safeActivities = activities && Array.isArray(activities.logs) ? activities : { logs: [], total: 0 };
  const safePlans = plans && Array.isArray(plans.plans) ? plans : { plans: [], total: 0 };
  const safeStats = stats || emptyStats;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md w-full">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchAdminData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 w-full sm:w-auto"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Management</h1>
              <p className="text-gray-600 text-sm sm:text-base hidden sm:block">Monitor users, subscriptions, and platform activity</p>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={fetchAdminData}
                className="bg-blue-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1 sm:space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Tab Navigation */}
        <div className="mb-6 sm:mb-8">
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: BarChart3 },
              { id: 'users', name: 'Users', icon: Users },
              { id: 'contracts', name: 'Documents', icon: FileText },
              { id: 'plans', name: 'Subscription Plans', icon: Package },
              { id: 'payments', name: 'Payments', icon: CreditCard },
              { id: 'lawyers', name: 'Lawyers', icon: Scale },
              { id: 'activity', name: 'Activity Logs', icon: Activity }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-5 h-5 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>

          {/* Mobile Navigation - Horizontal Scroll */}
          <div className="lg:hidden">
            <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
              {[
                { id: 'overview', name: 'Overview', icon: BarChart3 },
                { id: 'users', name: 'Users', icon: Users },
                { id: 'contracts', name: 'Documents', icon: FileText },
                { id: 'plans', name: 'Plans', icon: Package },
                { id: 'payments', name: 'Payments', icon: CreditCard },
                { id: 'lawyers', name: 'Lawyers', icon: Scale },
                { id: 'activity', name: 'Activity', icon: Activity }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  <span className="text-sm">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Dropdown Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute top-16 right-4 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-48">
              <div className="py-2">
                {[
                  { id: 'overview', name: 'Overview', icon: BarChart3 },
                  { id: 'users', name: 'Users', icon: Users },
                  { id: 'contracts', name: 'Documents', icon: FileText },
                  { id: 'plans', name: 'Subscription Plans', icon: Package },
                  { id: 'payments', name: 'Payments', icon: CreditCard },
                  { id: 'lawyers', name: 'Lawyers', icon: Scale },
                  { id: 'activity', name: 'Activity Logs', icon: Activity }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 ${
                      activeTab === tab.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 mr-3" />
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <OverviewTab stats={safeStats} />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <UsersTab 
            users={safeUsers} 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onDeactivateUser={(userId) => setUserToDeactivate(userId)}
            onActivateUser={(userId) => setUserToActivate(userId)}
            onDeleteUser={(userId) => setUserToDelete(userId)}
          />
        )}

        {/* Contracts Tab */}
        {activeTab === 'contracts' && (
          <ContractsTab 
            contracts={safeContracts}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            tabLabel="Documents"
          />
        )}

        {/* Subscription Plans Tab */}
        {activeTab === 'plans' && (
          <SubscriptionPlansTab 
            plans={safePlans}
            onCreatePlan={handleCreatePlan}
            onEditPlan={handleEditPlan}
            onDeletePlan={(planId) => setPlanToDelete(planId)}
            onShowAddModal={() => setShowAddPlanModal(true)}
            onShowEditModal={(plan) => {
              setEditingPlan(plan);
              setShowEditPlanModal(true);
            }}
          />
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <PaymentsTab 
            payments={safePayments}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        )}

        {activeTab === 'lawyers' && (
          <LawyersTab
            lawyers={lawyers.profiles || []}
            statusFilter={lawyerStatusFilter}
            onStatusFilterChange={(status) => {
              setLawyerStatusFilter(status);
              fetchLawyers(status);
            }}
            onApprove={handleApproveLawyer}
            onReject={handleRejectLawyer}
          />
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <ActivityTab 
            activities={safeActivities}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            userReports={userReports}
            onReviewReport={handleReviewReport}
          />
        )}
      </div>
    </div>

    {/* Add Plan Modal */}
    {showAddPlanModal && (
      <PlanModal
        isOpen={showAddPlanModal}
        onClose={() => setShowAddPlanModal(false)}
        onSave={handleCreatePlan}
        title="Add New Plan"
      />
    )}

    {/* Edit Plan Modal */}
    {showEditPlanModal && editingPlan && (
      <PlanModal
        isOpen={showEditPlanModal}
        onClose={() => {
          setShowEditPlanModal(false);
          setEditingPlan(null);
        }}
        onSave={(planData) => handleEditPlan(editingPlan.id, planData)}
        title="Edit Plan"
        initialData={editingPlan}
      />
    )}
    <ConfirmModal
      open={userToDeactivate != null}
      title="Deactivate user?"
      message="This user will no longer be able to log in. Their data will be kept. You can reactivate them later."
      confirmLabel="Deactivate"
      cancelLabel="Cancel"
      destructive
      onCancel={() => setUserToDeactivate(null)}
      onConfirm={async () => {
        if (userToDeactivate == null) return;
        await handleDeactivateUser(userToDeactivate);
        setUserToDeactivate(null);
      }}
    />
    <ConfirmModal
      open={userToActivate != null}
      title="Activate user?"
      message="This user will be able to log in again."
      confirmLabel="Activate"
      cancelLabel="Cancel"
      onCancel={() => setUserToActivate(null)}
      onConfirm={async () => {
        if (userToActivate == null) return;
        await handleActivateUser(userToActivate);
        setUserToActivate(null);
      }}
    />
    <ConfirmModal
      open={userToDelete != null}
      title="Permanently delete user?"
      message="This permanently deletes the user and all related documents, subscriptions, payments, and cloud/ImageKit files. This cannot be undone."
      confirmLabel="Delete forever"
      cancelLabel="Cancel"
      destructive
      onCancel={() => setUserToDelete(null)}
      onConfirm={async () => {
        if (userToDelete == null) return;
        await handleDeleteUser(userToDelete);
        setUserToDelete(null);
      }}
    />
    <ConfirmModal
      open={planToDelete != null}
      title="Delete plan?"
      message="Are you sure you want to delete this plan?"
      confirmLabel="Delete"
      cancelLabel="Cancel"
      destructive
      onCancel={() => setPlanToDelete(null)}
      onConfirm={async () => {
        if (planToDelete == null) return;
        await handleDeletePlan(planToDelete);
        setPlanToDelete(null);
      }}
    />
    </>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats }) => (
  <div className="space-y-8">
    {/* Stats Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        title="Total Users"
        value={stats.total_users ?? 0}
        icon={<Users className="w-8 h-8" />}
        color="blue"
      />
      <StatCard
        title="Total Contracts"
        value={stats.total_contracts ?? 0}
        icon={<FileText className="w-8 h-8" />}
        color="green"
      />
      <StatCard
        title="Total Revenue"
        value={`₦${(stats.total_revenue ?? 0).toLocaleString()}`}
        icon={<DollarSign className="w-8 h-8" />}
        color="purple"
      />
      <StatCard
        title="Avg Risk Score"
        value={`${stats.avg_risk_score ?? 0}%`}
        icon={<BarChart3 className="w-8 h-8" />}
        color="orange"
      />
    </div>

    {/* Today's Activity */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Activity</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Active Users</span>
            <span className="font-semibold text-gray-900">{stats.active_users_today ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Contracts Processed</span>
            <span className="font-semibold text-gray-900">{stats.contracts_processed_today ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Documents Today</span>
            <span className="font-semibold text-gray-900">{stats.documents_today ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Pending Lawyers</span>
            <span className="font-semibold text-gray-900">{stats.pending_lawyer_profiles ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Pending User Reports</span>
            <span className="font-semibold text-gray-900">{stats.pending_user_reports ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Successful Payments</span>
            <span className="font-semibold text-gray-900">{stats.successful_payments ?? 0}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Contract Types</h3>
        <div className="space-y-3">
          {(stats.top_risk_types || []).length === 0 ? (
            <p className="text-sm text-gray-500">No contract type data yet.</p>
          ) : (
            (stats.top_risk_types || []).map((risk, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">{(risk.risk_type || 'unknown').replace('_', ' ')}</span>
                <span className="font-semibold text-gray-900">{risk.count}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  </div>
);

// Users Tab Component
const UsersTab = ({
  users,
  searchTerm,
  setSearchTerm,
  onDeactivateUser,
  onActivateUser,
  onDeleteUser,
}) => (
  <div className="space-y-4 sm:space-y-6">
    {/* Search and Filter */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center space-x-4">
        <div className="relative flex-1">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>

    {/* Users Table - Desktop */}
    <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Users ({users.total})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.users
              .filter(user => 
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {user.full_name.charAt(0)}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-800">
                    {roleLabel(user.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.subscription_plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                    user.subscription_plan === 'professional' ? 'bg-blue-100 text-blue-800' :
                    user.subscription_plan === 'starter' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.subscription_plan}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.contracts_this_month}/{user.monthly_limit}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center gap-2">
                    {user.is_active ? (
                      <button
                        type="button"
                        title="Deactivate user"
                        onClick={() => onDeactivateUser(user.id)}
                        className="text-amber-600 hover:text-amber-800 p-1"
                      >
                        <UserX className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        title="Activate user"
                        onClick={() => onActivateUser(user.id)}
                        className="text-green-600 hover:text-green-800 p-1"
                      >
                        <UserCheck className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      title="Permanently delete user"
                      onClick={() => onDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Users Cards - Mobile */}
    <div className="lg:hidden">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Users ({users.total})
        </h3>
      </div>
      <div className="space-y-4">
        {users.users
          .filter(user => 
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map((user) => (
          <div key={user.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center flex-1">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {user.full_name.charAt(0)}
                </div>
                <div className="ml-3 flex-1">
                  <div className="font-medium text-gray-900 truncate">{user.full_name}</div>
                  <div className="text-sm text-gray-500 truncate">{user.email}</div>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-800">
                      {roleLabel(user.role)}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.subscription_plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                      user.subscription_plan === 'professional' ? 'bg-blue-100 text-blue-800' :
                      user.subscription_plan === 'starter' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {user.subscription_plan}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {user.is_active ? (
                  <button
                    type="button"
                    title="Deactivate user"
                    onClick={() => onDeactivateUser(user.id)}
                    className="text-amber-600 hover:text-amber-800 p-2"
                  >
                    <UserX className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    title="Activate user"
                    onClick={() => onActivateUser(user.id)}
                    className="text-green-600 hover:text-green-800 p-2"
                  >
                    <UserCheck className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  title="Permanently delete user"
                  onClick={() => onDeleteUser(user.id)}
                  className="text-red-600 hover:text-red-900 p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Usage:</span>
                <span className="ml-1 font-medium">{user.contracts_this_month}/{user.monthly_limit}</span>
              </div>
              <div>
                <span className="text-gray-500">Joined:</span>
                <span className="ml-1">{new Date(user.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Contracts Tab Component
const ContractsTab = ({ contracts, searchTerm, setSearchTerm, tabLabel = "Documents" }) => (
  <div className="space-y-4 sm:space-y-6">
    {/* Search */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder={`Search ${tabLabel.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>

    {/* Documents Table - Desktop */}
    <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {tabLabel} ({contracts.total})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contracts.contracts
              .filter(contract => 
                contract.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
                contract.user_email.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((contract) => (
              <tr key={contract.id}>
                <td className="px-6 py-4 align-top">
                  <div
                    className="text-sm font-medium text-gray-900 whitespace-normal max-w-sm truncate"
                    title={contract.original_filename || contract.filename}
                  >
                    {contract.original_filename || contract.filename}
                  </div>
                  <div className="text-sm text-gray-500">{(contract.file_size / 1024).toFixed(1)} KB</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{contract.user_name}</div>
                  <div className="text-sm text-gray-500">{contract.user_email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-700 capitalize">{contract.document_type}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    contract.status === 'completed' ? 'bg-green-100 text-green-800' :
                    contract.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {contract.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {contract.processed_at ? new Date(contract.processed_at).toLocaleDateString() : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Documents Cards - Mobile */}
    <div className="lg:hidden">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {tabLabel} ({contracts.total})
        </h3>
      </div>
      <div className="space-y-4">
        {contracts.contracts
          .filter(contract => 
            contract.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contract.user_email.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map((contract) => (
          <div key={contract.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4
                  className="font-medium text-gray-900 whitespace-normal max-w-sm truncate"
                  title={contract.original_filename || contract.filename}
                >
                  {contract.original_filename || contract.filename}
                </h4>
                <p className="text-sm text-gray-500">{(contract.file_size / 1024).toFixed(1)} KB</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  contract.status === 'completed' ? 'bg-green-100 text-green-800' :
                  contract.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {contract.status}
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">User:</span>
                <span className="text-sm font-medium text-gray-900">{contract.user_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Email:</span>
                <span className="text-sm text-gray-900 truncate ml-2">{contract.user_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Type:</span>
                <span className="text-sm font-medium text-gray-700 capitalize">{contract.document_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Processed:</span>
                <span className="text-sm text-gray-900">
                  {contract.processed_at ? new Date(contract.processed_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Subscription Plans Tab Component
const SubscriptionPlansTab = ({ plans, onShowAddModal, onShowEditModal, onDeletePlan }) => (
  <div className="space-y-4 sm:space-y-6">
    {/* Header with Add Button */}
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
      <h2 className="text-xl font-semibold text-gray-900">Subscription Plans</h2>
      <button
        onClick={onShowAddModal}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 w-full sm:w-auto"
      >
        <Plus className="w-4 h-4" />
        <span>Add Plan</span>
      </button>
    </div>

    {/* Plans Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {plans.plans.map((plan) => (
        <div key={plan.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{plan.display_name}</h3>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              plan.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {plan.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          
          <div className="space-y-2 mb-4">
            <div className="text-2xl font-bold text-gray-900">
              ₦{(plan.monthly_price / 100).toLocaleString()}
              <span className="text-sm font-normal text-gray-500">/month</span>
            </div>
            <div className="text-sm text-gray-600">
              ₦{(plan.yearly_price / 100).toLocaleString()}/year
            </div>
            <div className="text-sm text-gray-600">
              {plan.monthly_contracts} contracts/month
            </div>
          </div>

          <div className="flex space-x-2">
            <button 
              onClick={() => onShowEditModal(plan)}
              className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 flex items-center justify-center space-x-1"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
            <button 
              onClick={() => onDeletePlan(plan.id)}
              className="bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Payments Tab Component
const PaymentsTab = ({ payments, searchTerm, setSearchTerm }) => (
  <div className="space-y-4 sm:space-y-6">
    {/* Search */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search payments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>

    {/* Payments Table - Desktop */}
    <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Payments ({payments?.total || 0})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payments?.payments && payments.payments.length > 0 ? (
              payments.payments
                .filter(payment => 
                  payment.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  payment.plan_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  payment.transaction_reference?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((payment) => (
                <tr key={payment.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{payment.transaction_reference}</div>
                    <div className="text-sm text-gray-500">{payment.flutterwave_transaction_id || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{payment.customer_name}</div>
                    <div className="text-sm text-gray-500">{payment.customer_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 capitalize">{payment.plan_name} Plan</div>
                    <div className="text-sm text-gray-500 capitalize">{payment.billing_cycle}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ₦{(payment.amount / 100).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500">{payment.currency}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      payment.status === 'successful' ? 'bg-green-100 text-green-800' :
                      payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{new Date(payment.created_at).toLocaleDateString()}</div>
                    <div>{new Date(payment.created_at).toLocaleTimeString()}</div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <CreditCard className="w-12 h-12 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
                    <p className="text-sm text-gray-500">
                      It looks like you haven't received any payments yet.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Activity Tab Component
const ActivityTab = ({ activities, searchTerm, setSearchTerm, userReports = { reports: [], total: 0 }, onReviewReport }) => (
  <div className="space-y-4 sm:space-y-6">
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Pending User Reports ({userReports.total || 0})
        </h3>
        <p className="text-sm text-gray-500 mt-1">UGC reports from Find a Lawyer messaging.</p>
      </div>
      {(userReports.reports || []).length === 0 ? (
        <p className="p-4 sm:p-6 text-sm text-gray-500">No pending reports.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {(userReports.reports || []).map((report) => (
            <div key={report.id} className="p-4 sm:px-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 capitalize">{report.reason}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {report.reporter_email || `User #${report.reporter_id}`}
                  {' → '}
                  {report.reported_user_email || `User #${report.reported_user_id}`}
                </p>
                {report.details ? (
                  <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">{report.details}</p>
                ) : null}
                <p className="text-xs text-gray-400 mt-1">
                  Conversation #{report.conversation_id || '—'}
                  {report.message_id ? ` · Message #${report.message_id}` : ''}
                  {' · '}
                  {report.created_at ? new Date(report.created_at).toLocaleString() : ''}
                </p>
              </div>
              {onReviewReport ? (
                <button
                  type="button"
                  onClick={() => onReviewReport(report.id)}
                  className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                >
                  Mark reviewed
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Search */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search activity logs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>

    {/* Activity Logs Table - Desktop */}
    <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Activity Logs ({activities.total})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
        {activities.logs
          .filter(activity => {
            const description = (activity.description || activity.details || activity.action || '').toLowerCase();
            const email = (activity.user_email || '').toLowerCase();
            const term = searchTerm.toLowerCase();
            return description.includes(term) || email.includes(term);
          })
          .map((activity) => (
              <tr key={activity.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{activity.description || activity.details || activity.action}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{activity.user_name || '—'}</div>
                  <div className="text-sm text-gray-500">{activity.user_email || '—'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(activity.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
                </div>
                </div>

    {/* Activity Logs Cards - Mobile */}
    <div className="lg:hidden">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Activity Logs ({activities.total})
        </h3>
              </div>
      <div className="space-y-4">
        {activities.logs
          .filter(activity => {
            const description = (activity.description || activity.details || activity.action || '').toLowerCase();
            const email = (activity.user_email || '').toLowerCase();
            const term = searchTerm.toLowerCase();
            return description.includes(term) || email.includes(term);
          })
          .map((activity) => (
          <div key={activity.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center flex-1">
                <div className="font-medium text-gray-900 truncate">{activity.description || activity.details || activity.action}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">User:</span>
                <span className="ml-1 font-medium">{activity.user_name || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500">Email:</span>
                <span className="ml-1">{activity.user_email || '—'}</span>
          </div>
      </div>
            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Date:</span>
                <span className="ml-1">{new Date(activity.created_at).toLocaleDateString()}</span>
    </div>
  </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );

// Plan Modal Component
const PlanModal = ({ isOpen, onClose, onSave, title, initialData }) => {
  const [form, setForm] = React.useState({
    name: initialData?.name || '',
    display_name: initialData?.display_name || '',
    monthly_price: initialData?.monthly_price || '',
    yearly_price: initialData?.yearly_price || '',
    monthly_contracts: initialData?.monthly_contracts || '',
    features: initialData?.features ? JSON.stringify(initialData.features, null, 2) : '',
    is_active: initialData?.is_active ?? true,
  });
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    // Validate required fields
    if (!form.name || !form.display_name || !form.monthly_price || !form.yearly_price || !form.monthly_contracts) {
      setError('Please fill all required fields.');
      setSaving(false);
      return;
    }
    let featuresObj = {};
    try {
      featuresObj = form.features ? JSON.parse(form.features) : {};
    } catch (err) {
      setError('Features must be valid JSON.');
      setSaving(false);
      return;
    }
    const planData = {
      name: form.name,
      display_name: form.display_name,
      monthly_price: Number(form.monthly_price),
      yearly_price: Number(form.yearly_price),
      monthly_contracts: Number(form.monthly_contracts),
      features: featuresObj,
      is_active: !!form.is_active
    };
    try {
      await onSave(planData);
    } catch (err) {
      setError(err.message || 'Failed to save plan.');
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || '',
        display_name: initialData.display_name || '',
        monthly_price: initialData.monthly_price || '',
        yearly_price: initialData.yearly_price || '',
        monthly_contracts: initialData.monthly_contracts || '',
        features: initialData.features ? JSON.stringify(initialData.features, null, 2) : '',
        is_active: initialData.is_active ?? true,
      });
    } else {
      setForm({
        name: '',
        display_name: '',
        monthly_price: '',
        yearly_price: '',
        monthly_contracts: '',
        features: '',
        is_active: true,
      });
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700">
          <X className="w-6 h-6" />
          </button>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Plan Name *</label>
            <input name="name" value={form.name} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Display Name *</label>
            <input name="display_name" value={form.display_name} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" required />
          </div>
          <div className="flex space-x-2">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Monthly Price (kobo) *</label>
              <input name="monthly_price" type="number" value={form.monthly_price} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" required />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Yearly Price (kobo) *</label>
              <input name="yearly_price" type="number" value={form.yearly_price} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Monthly Contracts *</label>
            <input name="monthly_contracts" type="number" value={form.monthly_contracts} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Features (JSON)</label>
            <textarea name="features" value={form.features} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1 font-mono" rows={4} placeholder='{"AI Analysis": true, "Basic Reports": true}' />
          </div>
          <div className="flex items-center">
            <input name="is_active" type="checkbox" checked={form.is_active} onChange={handleChange} className="mr-2" />
            <label className="text-sm text-gray-700">Active</label>
          </div>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className="mr-3 px-4 py-2 rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving...' : 'Save Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Stat Card Component
const LawyersTab = ({ lawyers, statusFilter, onStatusFilterChange, onApprove, onReject }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <h3 className="text-lg font-semibold text-gray-900">Lawyer profiles</h3>
      <select
        value={statusFilter}
        onChange={(e) => onStatusFilterChange(e.target.value)}
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
      >
        <option value="all">All</option>
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
      </select>
    </div>
    {lawyers.length === 0 ? (
      <p className="text-gray-500 text-sm">No profiles in this queue.</p>
    ) : (
      <div className="space-y-4">
        {lawyers.map((profile) => (
          <div key={profile.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div>
                <h4 className="font-semibold text-gray-900">{profile.display_name}</h4>
                {profile.firm_name ? <p className="text-sm text-gray-600">{profile.firm_name}</p> : null}
                <p className="text-sm text-gray-700 mt-1">{profile.headline}</p>
                <p className="text-xs text-gray-500 mt-2">
                  {[profile.city, profile.country].filter(Boolean).join(', ')}
                  {profile.bar_number ? ` · SCN ${profile.bar_number}` : ''}
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(profile.practice_areas || []).map((area) => (
                    <span key={area} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">{area}</span>
                  ))}
                </div>
                {profile.rejection_reason ? (
                  <p className="text-xs text-red-600 mt-2">Note: {profile.rejection_reason}</p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0 items-center">
                {profile.profile_url && profile.status === 'approved' ? (
                  <a href={profile.profile_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-sm border rounded-lg text-blue-600">
                    View public
                  </a>
                ) : null}
                {profile.status === 'pending' ? (
                  <>
                    <button type="button" onClick={() => onApprove(profile.id)} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg">
                      Approve
                    </button>
                    <button type="button" onClick={() => onReject(profile.id)} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg">
                      Reject
                    </button>
                  </>
                ) : null}
                {profile.status === 'approved' ? (
                  <>
                    <span className="text-xs uppercase font-semibold text-emerald-700 px-2 py-1">Approved</span>
                    <button
                      type="button"
                      onClick={() => onReject(profile.id, { revoke: true })}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg"
                    >
                      Revoke approval
                    </button>
                  </>
                ) : null}
                {profile.status === 'rejected' ? (
                  <>
                    <span className="text-xs uppercase font-semibold text-red-700 px-2 py-1">Rejected</span>
                    <button type="button" onClick={() => onApprove(profile.id)} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg">
                      Approve
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-${color}-100 rounded-lg shadow-sm border border-${color}-200 p-4`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <div className={`w-8 h-8 text-${color}-600`}>{icon}</div>
        <div className="text-sm font-medium text-gray-900">{title}</div>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
    </div>
  </div>
);

export default AdminDashboard;